import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { Camera, Check, CircleCheck, CircleX, KeyRound, Search, X } from 'lucide-react'
import { lazy, Suspense, useEffect, useRef, useState, type FormEvent } from 'react'
import {
  linkAppAccount,
  previewEntry,
  settingsQuery,
  useEnter,
  visitKey,
  type EntryInput,
  type EntryResult,
  type Preview,
} from '@/api/access'
import { clientKeys } from '@/api/clients'
import { clientName, clientSearchQuery, type ClientLite } from '@/api/passes'
import { EntryShell, type EntryContext } from '@/app/access/entry-shell'
import { useCompanyCaller } from '@/app/company'
import { ClientPicker } from '@/app/passes/client-picker'
import { clockTime, defaultPass, firstEntryTab, refusalText, scanKind, suggestLocker } from '@/domain/access'
import { clientAbilities } from '@/domain/client-abilities'
import { errorText } from '@/domain/errors'
import { formatDate, t } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/button'
import { Avatar } from '@/ui/compact-row'
import { Field, wellClass } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { SidePanel } from '@/ui/overlays'
import { Spinner } from '@/ui/spinner'

const QrCamera = lazy(() => import('@/app/access/qr-camera'))

export const Route = createFileRoute('/_app/c/$orgId/entry/')({
  component: Desk,
})

/// «Рецепція» — `checkin ≥ edit`. Anyone else lands on the first tab they
/// may open.
function Desk() {
  const { orgId } = Route.useParams()
  return (
    <EntryShell orgId={orgId} tab="desk" allowed={(can) => can.see}>
      {(ctx) =>
        ctx.can.admit ? (
          <DeskScreen key={ctx.branch.id} {...ctx} />
        ) : firstEntryTab(ctx.can) === 'inside' ? (
          <Navigate to="/c/$orgId/entry/inside" params={{ orgId }} replace />
        ) : (
          <Navigate to="/c/$orgId/entry/settings" params={{ orgId }} replace />
        )
      }
    </EntryShell>
  )
}

/// What the right-hand card shows.
type Stage =
  | Readonly<{ kind: 'idle' }>
  | Readonly<{ kind: 'checking' }>
  | Readonly<{ kind: 'verdict'; preview: Extract<Preview, { identified: true }>; note?: string }>
  | Readonly<{ kind: 'result'; result: EntryResult; keyNotIssued?: boolean }>
  /// A card that names nobody: the search for the same digits is shown.
  | Readonly<{ kind: 'unknown'; reason: string; message: string | null; query: string }>
  | Readonly<{ kind: 'error'; message: string }>

/// How long a result stays before the desk is ready for the next person.
const SHOW_ALLOWED_MS = 5000
const SHOW_REFUSED_MS = 9000

/// The desk: ONE field that always has focus — a USB card reader types its
/// digits + Enter into it, a camera QR arrives the same way, a receptionist
/// types a name or a phone. A card or a picked client is PREVIEWED (each
/// pass's verdict, the key to hand) and let in with «Пустити»; a QR goes
/// straight to the entry, because a QR is spent by the first look (Q1).
function DeskScreen({ orgId, branch, timeZone, can }: EntryContext) {
  const { access } = useCompanyCaller(orgId)
  const canLink = clientAbilities(access).edit
  const queryClient = useQueryClient()
  const enter = useEnter(orgId)
  const field = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const [settled, setSettled] = useState('')
  const [stage, setStage] = useState<Stage>({ kind: 'idle' })
  const [camera, setCamera] = useState(false)
  const [linking, setLinking] = useState<string | null>(null)
  const c = t.access.desk

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(text.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [text])

  const searchText = stage.kind === 'unknown' ? stage.query : scanKind(settled) === 'qr' ? '' : settled
  const search = useQuery({ ...clientSearchQuery(orgId, searchText), enabled: searchText.length >= 2 })
  const results = searchText.length >= 2 ? (search.data ?? []) : []

  const focusField = () => window.requestAnimationFrame(() => field.current?.focus())
  const reset = () => {
    setStage({ kind: 'idle' })
    focusField()
  }

  // A reader types wherever the focus happens to be: a printable key that
  // lands on the page (not in another field) goes to the scan field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (!target || event.metaKey || event.ctrlKey || event.altKey || event.key.length !== 1) return
      if (target.closest('input, textarea, select, [role=dialog], [role=alertdialog]')) return
      field.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // A plain result clears itself; one that offers a next step waits.
  const resultActions = stage.kind === 'result' ? actionsFor(stage.result, can.override, canLink) : null
  useEffect(() => {
    if (stage.kind !== 'result' || (resultActions && (resultActions.link || resultActions.override))) return
    const timer = window.setTimeout(() => {
      setStage({ kind: 'idle' })
      field.current?.focus()
    }, stage.result.allow ? SHOW_ALLOWED_MS : SHOW_REFUSED_MS)
    return () => window.clearTimeout(timer)
  }, [stage, resultActions])

  const preview = async (body: { clientId?: string; scan?: string }, note?: string) => {
    setStage({ kind: 'checking' })
    try {
      const answer = await previewEntry(orgId, { branchId: branch.id, ...body })
      if (answer.identified) {
        setStage({ kind: 'verdict', preview: answer, ...(note ? { note } : {}) })
      } else {
        setStage({ kind: 'unknown', reason: answer.reason, message: answer.message, query: body.scan ?? '' })
      }
    } catch (error) {
      setStage({ kind: 'error', message: errorText(error) })
    }
    focusField()
  }

  /// Let in. A QR (and «Пустити все одно» from its result) has no preview to
  /// pick a key on: with numbered keys a key goes with it — the renter's own
  /// locker, else the lowest free one nobody rents (specs/access.md §10b). The
  /// board is read fresh: a stale one would offer a key somebody just took.
  const admit = async (body: Omit<EntryInput, 'branchId'>, autoKey = false) => {
    setStage({ kind: 'checking' })
    try {
      const result = await enter.mutateAsync({ branchId: branch.id, ...body })
      if (autoKey && result.allow && result.visitId && !result.lockerNumber) {
        const settings = await queryClient.fetchQuery({ ...settingsQuery(orgId, branch.id), staleTime: 0 }).catch(() => null)
        if (settings?.lockerMode === 'keys') {
          const number = suggestLocker(settings.lockers, result.clientId)
          const issued = number
            ? await visitKey(orgId, result.visitId, { action: 'issue', number }).then(
                (visit) => visit.lockerNumber,
                () => null,
              )
            : null
          setStage({ kind: 'result', result: { ...result, lockerNumber: issued }, keyNotIssued: !issued })
          focusField()
          return
        }
      }
      setStage({ kind: 'result', result })
    } catch (error) {
      setStage({ kind: 'error', message: errorText(error) })
    }
    focusField()
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const value = text.trim()
    // Enter on an empty field confirms what is on the card.
    if (!value) {
      if (stage.kind === 'verdict') document.getElementById('entry-admit')?.click()
      return
    }
    const kind = scanKind(value)
    if (kind === 'search') {
      // A name: Enter takes the first match once the search has caught up.
      const first = results[0]
      if (first && settled === value) pick(first)
      else setSettled(value)
      return
    }
    setText('')
    setSettled('')
    if (kind === 'qr') void admit({ scan: value }, true)
    else void preview({ scan: value })
  }

  const pick = (client: ClientLite) => {
    setText('')
    setSettled('')
    void preview({ clientId: client.id })
  }

  const showResults = (stage.kind === 'unknown' || (scanKind(settled) === 'search' && settled.length >= 2)) && searchText.length >= 2

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-8">
      <div className="flex min-w-0 flex-col gap-4">
        <form onSubmit={submit} className="flex flex-col gap-1.5">
          <label htmlFor="entry-scan" className="sr-only">
            {c.scanLabel}
          </label>
          <div className={cn(wellClass(false), 'min-h-14 [--well:var(--surface)] sm:min-h-14')}>
            <Search aria-hidden="true" className="ml-4 size-5 shrink-0 text-muted" />
            <input
              id="entry-scan"
              ref={field}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              enterKeyHint="go"
              placeholder={c.scanPlaceholder}
              aria-describedby="entry-scan-hint"
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setText('')
                  if (stage.kind !== 'checking') setStage({ kind: 'idle' })
                }
              }}
              className="h-14 min-w-0 flex-1 bg-transparent px-3 text-body text-text outline-none placeholder:text-muted"
            />
            {text ? (
              <button
                type="button"
                aria-label={c.clear}
                onClick={() => {
                  setText('')
                  field.current?.focus()
                }}
                className="inline-flex size-11 shrink-0 items-center justify-center rounded-[12px] text-muted hover:text-text"
              >
                <X className="size-[18px]" />
              </button>
            ) : null}
            <Button
              variant="secondary"
              compact
              className="mr-2"
              icon={<Camera aria-hidden="true" className="size-4" />}
              onClick={() => setCamera(true)}
              aria-label={c.camera}
            >
              <span className="max-sm:sr-only">{c.camera}</span>
            </Button>
          </div>
          <p id="entry-scan-hint" className="px-1 text-caption text-muted">
            {c.scanHint}
          </p>
        </form>

        {showResults ? (
          <ResultsList
            title={stage.kind === 'unknown' ? c.cardNotFound : c.found}
            clients={results}
            busy={search.isFetching}
            error={search.isError ? errorText(search.error) : null}
            onPick={pick}
          />
        ) : null}
      </div>

      <div className="min-w-0" aria-live="polite">
        {stage.kind === 'idle' ? (
          <div className="flex min-h-[220px] flex-col items-center justify-center rounded-[20px] px-6 py-10 text-center shadow-[inset_0_0_0_1px_var(--surface-3)]">
            <p className="text-callout font-semibold text-text">{c.idleTitle}</p>
            <p className="mt-1 max-w-[340px] text-caption text-text-2">{c.idleMessage}</p>
          </div>
        ) : stage.kind === 'checking' ? (
          <div className="flex min-h-[220px] items-center justify-center gap-2 rounded-[20px] bg-surface text-callout text-text-2">
            <Spinner className="size-4" />
            {c.checking}
          </div>
        ) : stage.kind === 'verdict' ? (
          <VerdictCard
            key={stage.preview.client.id}
            preview={stage.preview}
            note={stage.note}
            timeZone={timeZone}
            canOverride={can.override}
            busy={enter.isPending}
            onAdmit={(input) => void admit({ clientId: stage.preview.client.id, ...input })}
            onCancel={reset}
          />
        ) : stage.kind === 'result' ? (
          <ResultCard
            result={stage.result}
            keyNotIssued={stage.keyNotIssued ?? false}
            actions={resultActions}
            timed={!(resultActions?.link || resultActions?.override)}
            onLink={() => setLinking(stage.result.userId)}
            onOverride={() => stage.result.clientId && void admit({ clientId: stage.result.clientId, override: true }, true)}
            onDismiss={reset}
          />
        ) : stage.kind === 'unknown' ? (
          <ResultCard
            result={{
              allow: false,
              reason: stage.reason,
              message: stage.message,
              visitId: null,
              clientId: null,
              displayName: null,
              passId: null,
              lockerNumber: null,
              userId: null,
            }}
            keyNotIssued={false}
            actions={null}
            timed={false}
            onLink={() => undefined}
            onOverride={() => undefined}
            onDismiss={reset}
          />
        ) : (
          <InlineBanner kind="error" message={stage.message} action={{ label: c.dismiss, onClick: reset }} />
        )}
      </div>

      <SidePanel
        open={camera}
        onOpenChange={(open) => {
          setCamera(open)
          if (!open) focusField()
        }}
        title={c.cameraTitle}
        description={c.cameraMessage}
      >
        {camera ? (
          <Suspense
            fallback={
              <div className="flex aspect-[4/3] items-center justify-center rounded-[16px] bg-surface-2">
                <Spinner className="size-5 text-muted" />
              </div>
            }
          >
            <QrCamera
              onRead={(value) => {
                setCamera(false)
                const kind = scanKind(value)
                if (kind === 'qr') void admit({ scan: value.trim() }, true)
                else if (kind === 'card') void preview({ scan: value.trim() })
                else setText(value.trim())
              }}
            />
          </Suspense>
        ) : null}
      </SidePanel>

      {linking ? (
        <LinkPanel
          orgId={orgId}
          userId={linking}
          onClose={() => {
            setLinking(null)
            focusField()
          }}
          onLinked={(client) => {
            setLinking(null)
            void queryClient.invalidateQueries({ queryKey: clientKeys.all(orgId) })
            void preview({ clientId: client.id }, c.linked(clientName(client)))
          }}
        />
      ) : null}
    </div>
  )
}

type ResultActions = Readonly<{ link: boolean; override: boolean }>

/// What a refused entry lets the desk do next: link an app account nobody
/// tied to a client yet (§5), or let in somebody the door believes is
/// inside (A1, `checkin: manage`).
const actionsFor = (result: EntryResult, canOverride: boolean, canLink: boolean): ResultActions | null => {
  if (result.allow) return null
  return {
    link: result.reason === 'unknown_person' && Boolean(result.userId) && canLink,
    override: result.reason === 'already_inside' && Boolean(result.clientId) && canOverride,
  }
}

function ResultsList({
  title,
  clients,
  busy,
  error,
  onPick,
}: {
  title: string
  clients: readonly ClientLite[]
  busy: boolean
  error: string | null
  onPick: (client: ClientLite) => void
}) {
  const c = t.access.desk
  return (
    <section aria-label={title} className="overflow-hidden rounded-[16px] bg-surface">
      <div className="flex h-9 items-center justify-between px-4 text-caption font-medium text-muted shadow-[inset_0_-1px_0_var(--surface-3)]">
        <span>{title}</span>
        {busy ? <Spinner className="size-3.5" /> : null}
      </div>
      {error ? (
        <p className="px-4 py-3 text-caption text-danger">{error}</p>
      ) : clients.length === 0 ? (
        <p className="px-4 py-3 text-caption text-muted">{busy ? c.searching : c.none}</p>
      ) : (
        <ul className="max-h-[420px] overflow-y-auto py-1">
          {clients.map((client) => (
            <li key={client.id}>
              <button
                type="button"
                onClick={() => onPick(client)}
                className="flex min-h-12 w-full items-center gap-3 px-4 text-left transition-colors hover:bg-surface-2 focus-visible:bg-surface-2"
              >
                <Avatar name={clientName(client)} />
                <span className="flex min-w-0 flex-1 flex-col py-1.5">
                  <span className="truncate text-callout font-medium text-text">{clientName(client)}</span>
                  <span className="truncate text-caption text-muted tabular">
                    {[client.phone, client.cardNumber].filter(Boolean).join(' · ') || '—'}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/// Before the click: who, whether they are already inside, what every pass
/// says, the key to hand. The desk may pick another admitting pass and type
/// another key. «Пустити» is the page's one accent.
function VerdictCard({
  preview,
  note,
  timeZone,
  canOverride,
  busy,
  onAdmit,
  onCancel,
}: {
  preview: Extract<Preview, { identified: true }>
  note: string | undefined
  timeZone: string
  canOverride: boolean
  busy: boolean
  onAdmit: (input: { passId?: string; lockerNumber?: string; override?: boolean }) => void
  onCancel: () => void
}) {
  const c = t.access.desk
  const [passId, setPassId] = useState<string | null>(defaultPass(preview.passes))
  const [key, setKey] = useState(preview.suggestedLocker ?? '')
  const keys = preview.lockerMode === 'keys'
  const admitting = preview.passes.filter((p) => p.ok)
  const canAdmit = passId !== null && !preview.inside
  const input = () => ({
    ...(passId ? { passId } : {}),
    ...(keys && key.trim() ? { lockerNumber: key.trim() } : {}),
  })
  const contact = [preview.client.phone, preview.client.cardNumber].filter(Boolean).join(' · ')

  return (
    <section aria-label={preview.client.name} className="flex flex-col gap-5 rounded-[20px] bg-surface p-5 sm:p-6">
      {note ? <InlineBanner kind="done" message={note} /> : null}
      <div className="flex items-center gap-4">
        <Avatar name={preview.client.name} className="size-14 text-[18px]" />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-heading font-bold text-text">{preview.client.name}</h2>
          {contact ? <p className="truncate text-callout text-text-2 tabular">{contact}</p> : null}
        </div>
      </div>

      {preview.inside ? (
        <InlineBanner
          kind="stale"
          title={c.insideSince(clockTime(preview.inside.enteredAt, timeZone))}
          message={canOverride ? c.admitAnywayHint : t.access.refusals['already_inside'] ?? ''}
        />
      ) : null}

      {/* §10b: the client rents a locker here — said out loud at the desk,
          and with numbered keys its key is already in the field below. */}
      {preview.rental?.until ? (
        <p className="-mt-1 flex items-center gap-2 text-callout font-medium text-text">
          <KeyRound aria-hidden="true" className="size-4 shrink-0 text-text-2" />
          {c.rentedLocker(preview.rental.lockerNumber, formatDate(preview.rental.until))}
        </p>
      ) : null}

      <div className="flex flex-col gap-2">
        <h3 className="text-caption font-semibold text-muted">{c.passes}</h3>
        {preview.passes.length === 0 ? (
          <p className="text-callout text-text-2">{c.noPasses}</p>
        ) : (
          <ul role={admitting.length > 1 ? 'radiogroup' : undefined} aria-label={c.passes} className="row-list overflow-hidden rounded-[16px] bg-surface-2">
            {preview.passes.map((pass) => {
              const chosen = pass.id === passId
              const meta = [
                pass.endsOn ? c.endsOn(formatDate(pass.endsOn)) : null,
                pass.visitsLeft === null ? c.unlimited : c.visitsLeft(pass.visitsLeft),
              ]
                .filter(Boolean)
                .join(' · ')
              const body = (
                <>
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full',
                      pass.ok ? 'bg-success text-white' : 'bg-[color-mix(in_srgb,var(--danger)_14%,transparent)] text-danger',
                    )}
                  >
                    {pass.ok ? <Check className="size-3.5" strokeWidth={3} /> : <X className="size-3.5" strokeWidth={3} />}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-callout font-medium text-text">{pass.planName}</span>
                    <span className="text-caption text-text-2 tabular">{meta}</span>
                    {!pass.ok ? <span className="mt-0.5 text-caption text-danger">{refusalText(pass.reason, pass.message)}</span> : null}
                  </span>
                  {chosen ? <span className="shrink-0 text-caption font-semibold text-text-2">{c.willSpend}</span> : null}
                </>
              )
              return (
                <li key={pass.id} className="compact-row">
                  {pass.ok && admitting.length > 1 ? (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={chosen}
                      onClick={() => setPassId(pass.id)}
                      className={cn('flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-3', chosen && 'bg-surface-3')}
                    >
                      {body}
                    </button>
                  ) : (
                    <div className="flex items-start gap-3 px-4 py-3">{body}</div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        {preview.passes.length > 0 && admitting.length === 0 ? (
          <p className="text-callout text-danger">{refusalText(preview.passes[0]?.reason ?? 'no_pass', preview.passes[0]?.message)}</p>
        ) : null}
      </div>

      {keys && passId && (!preview.inside || canOverride) ? (
        <Field
          label={c.key}
          value={key}
          inputMode="text"
          autoComplete="off"
          className="max-w-[220px]"
          hint={
            preview.rental && preview.suggestedLocker === preview.rental.lockerNumber
              ? c.keyHintRented
              : preview.suggestedLocker
                ? c.keyHint
                : c.noFreeKey
          }
          onChange={(event) => setKey(event.target.value)}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {canAdmit ? (
          <Button id="entry-admit" variant="primary" className="h-12 px-6 text-body sm:h-12" busy={busy} onClick={() => onAdmit(input())}>
            {busy ? c.admitting : c.admit}
          </Button>
        ) : null}
        {preview.inside && canOverride && passId ? (
          <Button id="entry-admit" variant="secondary" className="h-12 sm:h-12" busy={busy} onClick={() => onAdmit({ ...input(), override: true })}>
            {c.admitAnyway}
          </Button>
        ) : null}
        <Button variant="quiet" onClick={onCancel}>
          {c.cancel}
        </Button>
      </div>
    </section>
  )
}

/// After the click (or a QR): green «Можна заходити — Олена Коваль, ключ 12»
/// or red with the reason sentence. A plain one clears itself — the thin bar
/// shows how long it stays.
function ResultCard({
  result,
  keyNotIssued,
  actions,
  timed,
  onLink,
  onOverride,
  onDismiss,
}: {
  result: EntryResult
  keyNotIssued: boolean
  actions: ResultActions | null
  timed: boolean
  onLink: () => void
  onOverride: () => void
  onDismiss: () => void
}) {
  const c = t.access.desk
  const name = result.displayName
  const Icon = result.allow ? CircleCheck : CircleX
  return (
    <section
      role={result.allow ? 'status' : 'alert'}
      className={cn(
        'relative flex flex-col gap-4 overflow-hidden rounded-[20px] p-5 sm:p-6',
        result.allow
          ? 'bg-[color-mix(in_srgb,var(--success)_14%,var(--surface))]'
          : 'bg-[color-mix(in_srgb,var(--danger)_12%,var(--surface))]',
      )}
    >
      <div className="flex items-start gap-4">
        <Icon
          aria-hidden="true"
          className={cn('size-10 shrink-0', result.allow ? 'text-success' : 'text-danger')}
          strokeWidth={2}
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-heading font-bold text-text">{result.allow ? c.allowedTitle : c.refusedTitle}</h2>
          {result.allow ? (
            <p className="mt-1 text-body text-text">
              {name && result.lockerNumber ? c.allowedKey(name, result.lockerNumber) : (name ?? '')}
            </p>
          ) : (
            <>
              {name ? <p className="mt-1 text-body font-medium text-text">{name}</p> : null}
              <p className="mt-1 text-body text-text-2">
                {result.reason === 'unknown_person' && result.userId ? c.appNotLinked : refusalText(result.reason, result.message)}
              </p>
            </>
          )}
          {result.allow && keyNotIssued ? <p className="mt-2 text-caption text-text-2">{c.keyNotIssued}</p> : null}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {actions?.link ? (
          <Button variant="primary" onClick={onLink}>
            {c.link}
          </Button>
        ) : null}
        {actions?.override ? (
          <Button variant="secondary" onClick={onOverride}>
            {c.admitAnyway}
          </Button>
        ) : null}
        <Button variant="quiet" className={actions?.link || actions?.override ? undefined : '-ml-3.5'} onClick={onDismiss}>
          {c.dismiss}
        </Button>
      </div>
      {timed ? (
        <div
          aria-hidden="true"
          className={cn(
            'absolute inset-x-0 bottom-0 h-1 origin-left motion-safe:animate-[entry-countdown_linear_forwards]',
            result.allow ? 'bg-success/50' : 'bg-danger/40',
          )}
          style={{ animationDuration: `${result.allow ? SHOW_ALLOWED_MS : SHOW_REFUSED_MS}ms` }}
        />
      ) : null}
    </section>
  )
}

/// §5: tie an app account (the QR's user) to the club's client, so the next
/// scan names them. `clients: edit`.
function LinkPanel({
  orgId,
  userId,
  onClose,
  onLinked,
}: {
  orgId: string
  userId: string
  onClose: () => void
  onLinked: (client: ClientLite) => void
}) {
  const c = t.access.desk
  const [client, setClient] = useState<ClientLite | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <SidePanel
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={c.linkTitle}
      description={c.linkMessage}
      onSubmit={(event) => {
        event.preventDefault()
        if (!client) return
        setBusy(true)
        setError(null)
        linkAppAccount(orgId, client.id, userId).then(
          () => onLinked(client),
          (failure: unknown) => {
            setBusy(false)
            setError(errorText(failure))
          },
        )
      }}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t.app.cancel}
          </Button>
          <Button type="submit" variant="primary" busy={busy} disabled={!client}>
            {busy ? c.linking : c.linkSubmit}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <ClientPicker orgId={orgId} value={client} onChange={setClient} label={c.linkClient} />
        {error ? <InlineBanner kind="error" message={error} /> : null}
      </div>
    </SidePanel>
  )
}
